import { useContext, useEffect, useRef, useState } from "react";
import { t } from "i18next";
import {
    Add,
    ArrowDown2,
    DocumentText,
    MagicStar,
    Trash,
} from "iconsax-react";
import DBButton from "../../../../component/global/DBButton";
import DBConfirmModal from "../../../../component/global/DBConfirmModal";
import DBFieldBlock from "../../../../component/global/DBFieldBlock";
import DBInput from "../../../../component/global/DBInput";
import DBModal from "../../../../component/global/DBModal";
import DBSelect from "../../../../component/global/DBSelect";
import DBSpinner from "../../../../component/global/DBSpinner";
import DBTextArea from "../../../../component/global/DBTextArea";
import { ToastContext } from "../../../../component/global/ToastContext";
import { useRequest } from "../../../../hooks/helperData/useRequest";
import {
    COMPANY_DETAIL,
    SETTING_AI_EXPERIENCE_ACTIVE,
    SETTING_AI_EXPERIENCE_SESSIONS,
} from "../../../../helperData/ApiAddress";
import { AiCoinHint, isInsufficientCoins, useAiCoinGate } from "../../../../features/ai-billing/AiCoinGate";
import BusinessTypeSelect from "../../../../component/global/BusinessTypeSelect";
import { get, patch } from "../../../../controlers/global/ConfigRequest";
import { selectedBusinessTypeIds } from "../../../profile/companyProfilePayload";

const PHASES = ["questions", "grouping", "features", "forms", "confirm"];
const PHASE_QUESTIONS = 0;
const PHASE_GROUPING = 1;
const PHASE_FEATURES = 2;
const PHASE_FORMS = 3;
const PHASE_CONFIRM = 4;

const INITIAL_ANSWERS = {
    business_description: "",
    business_type_ids: [],
    offering_type: "mixed",
    offerings: "",
    customers: "",
    grouping_preference: "",
    exclusions: "",
    max_depth: 3,
    feedback_goal: "",
    response_style: "mixed",
    expertise_level: "general",
};

const FEATURE_TYPE_OPTIONS = [
    { value: 1, label: t("ai.experience.feature.type.1") },
    { value: 4, label: t("ai.experience.feature.type.4") },
    { value: 5, label: t("ai.experience.feature.type.5") },
    { value: 6, label: t("ai.experience.feature.type.6") },
];

const CRITERIA_TYPE_LABELS = {
    1: "ai.experience.criteria.type.1",
    2: "ai.experience.criteria.type.2",
    4: "ai.experience.criteria.type.4",
    5: "ai.experience.criteria.type.5",
};

function flattenTree(nodes, prefix = "") {
    const flat = [];
    (nodes || []).forEach((node, index) => {
        const key = `${prefix}${index}`;
        flat.push({ key, name: node.name, description: node.description });
        flat.push(...flattenTree(node.children || [], `${key}.`));
    });
    return flat;
}

const hasEmptyName = (nodes) =>
    (nodes || []).some(
        (node) =>
            !node.name.trim() ||
            (node.children?.length > 0 && hasEmptyName(node.children))
    );

const optionsToText = (options) => {
    if (!options) return "";
    try {
        return JSON.parse(options)
            .map((option) => option.label)
            .join("، ");
    } catch {
        return "";
    }
};

const textToOptions = (text) => {
    const items = text
        .split(/[،,]/)
        .map((item) => item.trim())
        .filter(Boolean);
    if (!items.length) return null;
    return JSON.stringify(items.map((item) => ({ value: item, label: item })));
};

function EditableTreeNode({ node, nodeKey, selected, onToggleSelect, onChange, onDelete }) {
    const isSelected = selected.has(nodeKey);
    return (
        <li>
            <div className="ai-tree-node">
                <label className="ai-tree-check">
                    <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => onToggleSelect(nodeKey)}
                        aria-label={t("ai.experience.grouping.select.all")}
                    />
                </label>
                <DBInput
                    value={node.name}
                    size="large"
                    onChange={(event) =>
                        onChange({ ...node, name: event.target.value })
                    }
                />
                <span className={`ai-type ai-type-${node.typ}`}>
                    {node.typ === 1
                        ? t("item.type.service")
                        : t("item.type.item")}
                </span>
                <button
                    type="button"
                    className="ai-tree-action"
                    aria-label={t("ai.experience.grouping.add.child")}
                    title={t("ai.experience.grouping.add.child")}
                    onClick={() =>
                        onChange({
                            ...node,
                            children: [
                                ...(node.children || []),
                                {
                                    name: "",
                                    description: "",
                                    typ: node.typ,
                                    children: [],
                                },
                            ],
                        })
                    }
                >
                    <Add size={18} aria-hidden="true" />
                </button>
                <button
                    type="button"
                    className="ai-tree-delete"
                    aria-label={t("ai.itemtype.remove")}
                    title={t("ai.itemtype.remove")}
                    onClick={onDelete}
                >
                    <Trash size={18} aria-hidden="true" />
                </button>
            </div>
            {node.children?.length > 0 && (
                <ul>
                    {node.children.map((child, index) => (
                        <EditableTreeNode
                            key={`${nodeKey}.${index}`}
                            node={child}
                            nodeKey={`${nodeKey}.${index}`}
                            selected={selected}
                            onToggleSelect={onToggleSelect}
                            onChange={(updatedChild) =>
                                onChange({
                                    ...node,
                                    children: node.children.map(
                                        (item, itemIndex) =>
                                            itemIndex === index
                                                ? updatedChild
                                                : item
                                    ),
                                })
                            }
                            onDelete={() =>
                                onChange({
                                    ...node,
                                    children: node.children.filter(
                                        (_, childIndex) =>
                                            childIndex !== index
                                    ),
                                })
                            }
                        />
                    ))}
                </ul>
            )}
        </li>
    );
}

function FeatureRow({ feature, onChange, onDelete }) {
    return (
        <div className="ai-feature-row">
            <DBInput
                value={feature.name}
                onChange={(event) =>
                    onChange({ ...feature, name: event.target.value })
                }
            />
            <DBSelect
                size="large"
                value={feature.feature_type}
                onChange={(value) =>
                    onChange({
                        ...feature,
                        feature_type: value,
                        options: value === 5 ? feature.options : null,
                    })
                }
                options={FEATURE_TYPE_OPTIONS}
            />
            {feature.feature_type === 5 && (
                <DBInput
                    value={optionsToText(feature.options)}
                    placeholder={t("ai.experience.feature.options.placeholder")}
                    onChange={(event) =>
                        onChange({
                            ...feature,
                            options: textToOptions(event.target.value),
                        })
                    }
                />
            )}
            <button
                type="button"
                className="ai-tree-delete"
                aria-label={t("ai.itemtype.remove")}
                title={t("ai.itemtype.remove")}
                onClick={onDelete}
            >
                <Trash size={18} aria-hidden="true" />
            </button>
        </div>
    );
}

function GroupAccordion({ title, badge, children, defaultOpen = false }) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className={`ai-accordion ${open ? "open" : ""}`}>
            <button
                type="button"
                className="ai-accordion-header"
                onClick={() => setOpen(!open)}
                aria-expanded={open}
            >
                <span className="ai-accordion-title">{title}</span>
                {badge && <span className="preview-node-badge">{badge}</span>}
                <ArrowDown2
                    size={16}
                    aria-hidden="true"
                    className="preview-node-chevron"
                />
            </button>
            {open && <div className="ai-accordion-body">{children}</div>}
        </div>
    );
}

const currentCompanyId = () =>
    window.sessionStorage.getItem("usercompany");

const persistCompanyBusinessTypes = (ids) => {
    const companyId = currentCompanyId();
    if (!companyId) return;
    patch(COMPANY_DETAIL.replace("{id}", companyId), {
        business_types: ids,
        category: ids[0] || null,
    }).catch(() => {});
};

export default function AIExperienceModal({ showModal, onHide, onSuccess }) {
    const toast = useContext(ToastContext);
    const [phase, setPhase] = useState(PHASE_QUESTIONS);
    const [questionStep, setQuestionStep] = useState(0);
    const [answers, setAnswers] = useState(INITIAL_ANSWERS);
    const [mode, setMode] = useState("append");
    const [session, setSession] = useState(null);
    const [pendingSession, setPendingSession] = useState(null);
    const [resumePrompt, setResumePrompt] = useState(false);
    const [tree, setTree] = useState(null);
    const [selected, setSelected] = useState(new Set());
    const [features, setFeatures] = useState(null);
    const [forms, setForms] = useState(null);
    const [feedbackText, setFeedbackText] = useState("");
    const [error, setError] = useState("");
    const [formProgress, setFormProgress] = useState(null);
    const [featureProgress, setFeatureProgress] = useState(null);
    const [abandonConfirm, setAbandonConfirm] = useState(false);
    const autoRunRef = useRef("");

    const [getActive] = useRequest(SETTING_AI_EXPERIENCE_ACTIVE, "GET");
    const [createSession, createLoader] = useRequest(
        SETTING_AI_EXPERIENCE_SESSIONS,
        "POST"
    );
    const [patchSession, patchLoader] = useRequest(
        SETTING_AI_EXPERIENCE_SESSIONS,
        "PATCH"
    );
    const [deleteSession] = useRequest(SETTING_AI_EXPERIENCE_SESSIONS, "DELETE");
    const [genTree, genTreeLoader] = useRequest(
        SETTING_AI_EXPERIENCE_SESSIONS,
        "POST"
    );
    const [genFeatures, genFeaturesLoader] = useRequest(
        SETTING_AI_EXPERIENCE_SESSIONS,
        "POST"
    );
    const [genForms, genFormsLoader] = useRequest(
        SETTING_AI_EXPERIENCE_SESSIONS,
        "POST"
    );
    const [applySession, applyLoader] = useRequest(
        SETTING_AI_EXPERIENCE_SESSIONS,
        "POST"
    );
    const { confirmAndRun, openFromError, gate, isOpen: coinGateOpen } = useAiCoinGate();

    const generating =
        genTreeLoader || genFeaturesLoader || genFormsLoader;

    const flatGroups = tree ? flattenTree(tree) : [];
    const groupNames = flatGroups.reduce(
        (acc, group) => ({ ...acc, [group.key]: group.name }),
        {}
    );
    const selectedKeys = flatGroups
        .map((group) => group.key)
        .filter((key) => selected.has(key));

    const resetAll = () => {
        setPhase(PHASE_QUESTIONS);
        setQuestionStep(0);
        setAnswers(INITIAL_ANSWERS);
        setMode("append");
        setSession(null);
        setPendingSession(null);
        setResumePrompt(false);
        setTree(null);
        setSelected(new Set());
        setFeatures(null);
        setForms(null);
        setFeedbackText("");
        setError("");
        setFormProgress(null);
        setFeatureProgress(null);
        autoRunRef.current = "";
    };

    const loadFromSession = (draft) => {
        setSession(draft);
        setAnswers((current) => {
            const draftAnswers = draft.answers || {};
            const draftTypes = selectedBusinessTypeIds({
                business_types: draftAnswers.business_type_ids,
            });
            return {
                ...INITIAL_ANSWERS,
                ...draftAnswers,
                business_type_ids: draftTypes.length
                    ? draftTypes
                    : current.business_type_ids,
            };
        });
        setMode(draft.mode || "append");
        setTree(draft.tree || null);
        setFeatures(draft.features || null);
        setForms(draft.forms || null);
        if (draft.tree) {
            setSelected(
                new Set(flattenTree(draft.tree).map((group) => group.key))
            );
        }
        if (draft.forms && Object.keys(draft.forms).length) {
            setPhase(PHASE_FORMS);
        } else if (draft.features) {
            setPhase(PHASE_FEATURES);
        } else if (draft.tree) {
            setPhase(PHASE_GROUPING);
        } else {
            setPhase(PHASE_QUESTIONS);
        }
    };

    useEffect(() => {
        if (!showModal) return;
        resetAll();
        const companyId = currentCompanyId();
        const companyPromise = companyId
            ? get(COMPANY_DETAIL.replace("{id}", companyId)).catch(() => null)
            : Promise.resolve(null);
        Promise.all([getActive().catch(() => null), companyPromise]).then(
            ([response, company]) => {
                const companyIds = selectedBusinessTypeIds(company || {});
                if (companyIds.length) {
                    setAnswers((current) => ({
                        ...current,
                        business_type_ids: current.business_type_ids?.length
                            ? current.business_type_ids
                            : companyIds,
                    }));
                }
                if (response?.session) {
                    setPendingSession(response.session);
                    setResumePrompt(true);
                }
            }
        );
    }, [showModal]);

    const updateAnswer = (name, value) => {
        setAnswers({ ...answers, [name]: value });
        setError("");
        if (name === "business_type_ids") {
            persistCompanyBusinessTypes(value);
        }
    };

    const createSessionHandler = (force = false) =>
        createSession({ answers, mode, force })
            .then((draft) => {
                setSession(draft);
                return draft;
            })
            .catch((requestError) => {
                const data = requestError.response?.data;
                if (requestError.response?.status === 409 && data?.session) {
                    setPendingSession(data.session);
                    setResumePrompt(true);
                    return null;
                }
                if (requestError.response?.status === 409) {
                    toast(t("ai.experience.mode.replace.blocked"), "warning");
                    setMode("append");
                    return null;
                }
                toast(t("ai.experience.generate.error"), "danger");
                return null;
            });

    const generateTree = (feedback) => {
        if (!session) return;
        setError("");
        confirmAndRun(
            "item_type_tree",
            () => {
                genTree(
                    feedback ? { feedback } : {},
                    null,
                    `${session.id}/generate-tree`
                )
                    .then((response) => {
                        setTree(response.tree);
                        setSelected(
                            new Set(
                                flattenTree(response.tree).map(
                                    (group) => group.key
                                )
                            )
                        );
                        setFeatures(null);
                        setForms(null);
                        setFeedbackText("");
                    })
                    .catch((e) => {
                        if (isInsufficientCoins(e)) {
                            openFromError(e);
                            return;
                        }
                        toast(t("ai.experience.generate.error"), "danger");
                    });
            }
        );
    };

    const generateFeatures = async (keys, feedback) => {
        if (!session || !keys.length) return;
        setError("");
        const targets = feedback
            ? keys
            : keys.filter((key) => !(features?.[key]?.length));
        if (!targets.length) return;
        confirmAndRun(
            { operation: "group_features", quantity: targets.length },
            async () => {
                for (let index = 0; index < targets.length; index++) {
                    const key = targets[index];
                    setFeatureProgress({
                        current: index + 1,
                        total: targets.length,
                        name: groupNames[key] || "",
                    });
                    try {
                        const response = await genFeatures(
                            {
                                selected: [key],
                                ...(feedback ? { feedback } : {}),
                            },
                            null,
                            `${session.id}/generate-features`
                        );
                        setFeatures((previous) => ({
                            ...(previous || {}),
                            ...response.features,
                        }));
                    } catch (e) {
                        if (isInsufficientCoins(e)) {
                            openFromError(e);
                        } else {
                            toast(t("ai.experience.generate.error"), "danger");
                        }
                        break;
                    }
                }
                setFeatureProgress(null);
                if (feedback) setFeedbackText("");
            }
        );
    };

    const generateForms = async (keys, feedback) => {
        if (!session || !keys.length) return;
        setError("");
        const targets = feedback
            ? keys
            : keys.filter((key) => !(forms && forms[key]));
        if (!targets.length) return;
        confirmAndRun(
            { operation: "form_structure", quantity: targets.length },
            async () => {
                for (let index = 0; index < targets.length; index++) {
                    const key = targets[index];
                    setFormProgress({
                        current: index + 1,
                        total: targets.length,
                        name: groupNames[key] || "",
                    });
                    try {
                        const response = await genForms(
                            {
                                group_key: key,
                                ...(feedback ? { feedback } : {}),
                            },
                            null,
                            `${session.id}/generate-forms`
                        );
                        setForms((previous) => ({
                            ...(previous || {}),
                            [key]: response.form,
                        }));
                    } catch (e) {
                        if (isInsufficientCoins(e)) {
                            openFromError(e);
                        } else {
                            toast(t("ai.experience.generate.error"), "danger");
                        }
                        break;
                    }
                }
                setFormProgress(null);
                if (feedback) setFeedbackText("");
            }
        );
    };

    // Auto-run generation when entering a phase with missing content
    // (covers draft resume). Guarded so failures don't retry in a loop.
    useEffect(() => {
        if (!showModal || !session || coinGateOpen) return;
        const runKey = `${phase}:${session.id}:${tree ? "t" : ""}`;
        if (autoRunRef.current === runKey) return;
        if (phase === PHASE_GROUPING && !tree && !genTreeLoader) {
            autoRunRef.current = runKey;
            generateTree();
        } else if (phase === PHASE_FEATURES && tree && !genFeaturesLoader) {
            const missing = selectedKeys.filter(
                (key) => !(features?.[key]?.length)
            );
            if (missing.length) {
                autoRunRef.current = runKey;
                generateFeatures(missing);
            }
        } else if (phase === PHASE_FORMS && tree && !genFormsLoader) {
            const missing = selectedKeys.filter(
                (key) => !(forms && forms[key])
            );
            if (missing.length) {
                autoRunRef.current = runKey;
                generateForms(selectedKeys);
            }
        }
    });

    const validateQuestionStep = () => {
        if (questionStep === 0) {
            if (
                !selectedBusinessTypeIds({
                    business_types: answers.business_type_ids,
                }).length
            ) {
                setError(t("ai.itemtype.q1.types.required"));
                return false;
            }
            return true;
        }
        const requiredFields = [
            null,
            "offerings",
            "customers",
            "grouping_preference",
            null,
            "feedback_goal",
        ];
        const field = requiredFields[questionStep];
        if (field && !String(answers[field] || "").trim()) {
            setError(t("ai.experience.required"));
            return false;
        }
        return true;
    };

    const startGeneration = async () => {
        if (!validateQuestionStep()) return;
        setError("");
        let draft = session;
        if (!draft) {
            draft = await createSessionHandler(false);
            if (!draft) return;
        } else {
            await patchSession(
                { answers, mode },
                null,
                `${draft.id}`
            ).catch(() => {
                toast(t("ai.experience.save.draft.error"), "danger");
            });
        }
        autoRunRef.current = "";
        setPhase(PHASE_GROUPING);
        if (draft.tree && session) {
            // answers changed after a previous generation: regenerate
            generateTree();
        }
    };

    const saveDraftPart = (payload) =>
        patchSession(payload, null, `${session.id}`).catch(() => {
            toast(t("ai.experience.save.draft.error"), "danger");
            throw new Error("draft-save-failed");
        });

    const goNext = async () => {
        setError("");
        if (phase === PHASE_GROUPING) {
            if (!tree?.length || hasEmptyName(tree)) {
                setError(t("ai.itemtype.tree.required"));
                return;
            }
            if (!selectedKeys.length) {
                setError(t("ai.experience.grouping.selected.required"));
                return;
            }
            await saveDraftPart({ tree });
            autoRunRef.current = "";
            setPhase(PHASE_FEATURES);
        } else if (phase === PHASE_FEATURES) {
            await saveDraftPart({ features: features || {} });
            autoRunRef.current = "";
            setPhase(PHASE_FORMS);
        } else if (phase === PHASE_FORMS) {
            await saveDraftPart({ forms: forms || {} });
            autoRunRef.current = "";
            setPhase(PHASE_CONFIRM);
        }
    };

    const goPrevious = () => {
        setError("");
        setFeedbackText("");
        if (phase === PHASE_QUESTIONS) {
            setQuestionStep(Math.max(0, questionStep - 1));
        } else {
            setPhase(phase - 1);
        }
    };

    const finalApply = async () => {
        setError("");
        try {
            await saveDraftPart({
                tree,
                features: features || {},
                forms: forms || {},
            });
        } catch {
            return;
        }
        applySession({}, null, `${session.id}/apply`)
            .then((response) => {
                toast(t("ai.experience.save.success"), "success");
                onHide();
                onSuccess?.(response);
            })
            .catch(() => toast(t("ai.experience.save.error"), "danger"));
    };

    const saveAndContinueLater = async () => {
        if (!session || !tree?.length || hasEmptyName(tree)) {
            setError(t("ai.itemtype.tree.required"));
            return;
        }
        setError("");
        const hasFeatureContent = selectedKeys.some(
            (key) => (features?.[key] || []).length > 0
        );
        const hasFormContent = selectedKeys.some((key) => Boolean(forms?.[key]));
        let upTo = "grouping";
        const payload = { tree };
        if (phase >= PHASE_FEATURES) {
            payload.features = features || {};
            if (hasFeatureContent) upTo = "features";
        }
        if (phase >= PHASE_FORMS) {
            payload.forms = forms || {};
            if (hasFormContent) upTo = "forms";
        }
        try {
            await saveDraftPart(payload);
        } catch {
            return;
        }
        applySession({ up_to: upTo }, null, `${session.id}/apply`)
            .then((response) => {
                toast(t("ai.experience.save.later.success"), "success");
                onSuccess?.(response);
                onHide();
            })
            .catch(() => toast(t("ai.experience.save.error"), "danger"));
    };

    const abandonDraft = () => {
        setAbandonConfirm(false);
        if (!session) {
            onHide();
            return;
        }
        deleteSession(null, null, `${session.id}`)
            .catch(() => {})
            .finally(() => {
                resetAll();
                onHide();
            });
    };

    const toggleSelect = (key) => {
        const next = new Set(selected);
        if (next.has(key)) {
            next.delete(key);
        } else {
            next.add(key);
        }
        setSelected(next);
    };

    const renderQuestion = () => {
        switch (questionStep) {
            case 0:
                return (
                    <div className="ai-question-fields">
                        <DBFieldBlock
                            title={t("ai.itemtype.q1")}
                            required
                            error={error}
                        >
                            <BusinessTypeSelect
                                value={answers.business_type_ids}
                                onChange={(ids) =>
                                    updateAnswer("business_type_ids", ids)
                                }
                                hint={t(
                                    "Organizational.Profile.BusinessType.hint"
                                )}
                            />
                        </DBFieldBlock>
                        <DBFieldBlock title={t("ai.itemtype.q1.extra")}>
                            <DBTextArea
                                rows={3}
                                value={answers.business_description}
                                placeholder={t(
                                    "ai.itemtype.q1.extra.placeholder"
                                )}
                                onChange={(event) =>
                                    updateAnswer(
                                        "business_description",
                                        event.target.value
                                    )
                                }
                            />
                        </DBFieldBlock>
                    </div>
                );
            case 1:
                return (
                    <div className="ai-question-fields">
                        <div className="ai-step-grid">
                            <DBFieldBlock title={t("ai.itemtype.offering.type")}>
                                <DBSelect
                                    size="large"
                                    value={answers.offering_type}
                                    onChange={(value) =>
                                        updateAnswer("offering_type", value)
                                    }
                                    options={[
                                        {
                                            value: "product",
                                            label: t("item.type.item"),
                                        },
                                        {
                                            value: "service",
                                            label: t("item.type.service"),
                                        },
                                        {
                                            value: "mixed",
                                            label: t("ai.itemtype.mixed"),
                                        },
                                    ]}
                                />
                            </DBFieldBlock>
                        </div>
                        <DBFieldBlock
                            title={t("ai.itemtype.q2")}
                            required
                            error={error}
                        >
                            <DBTextArea
                                rows={4}
                                value={answers.offerings}
                                placeholder={t("ai.itemtype.q2.placeholder")}
                                onChange={(event) =>
                                    updateAnswer(
                                        "offerings",
                                        event.target.value
                                    )
                                }
                            />
                        </DBFieldBlock>
                    </div>
                );
            case 2:
                return (
                    <DBFieldBlock
                        title={t("ai.itemtype.q3")}
                        required
                        error={error}
                    >
                        <DBTextArea
                            rows={4}
                            value={answers.customers}
                            placeholder={t("ai.itemtype.q3.placeholder")}
                            onChange={(event) =>
                                updateAnswer("customers", event.target.value)
                            }
                        />
                    </DBFieldBlock>
                );
            case 3:
                return (
                    <div className="ai-question-fields">
                        <div className="ai-step-grid">
                            <DBFieldBlock
                                title={t("ai.itemtype.q4")}
                                required
                                error={error}
                            >
                                <DBTextArea
                                    rows={4}
                                    value={answers.grouping_preference}
                                    placeholder={t("ai.itemtype.q4.placeholder")}
                                    onChange={(event) =>
                                        updateAnswer(
                                            "grouping_preference",
                                            event.target.value
                                        )
                                    }
                                />
                            </DBFieldBlock>
                            <DBFieldBlock title={t("ai.itemtype.exclusions")}>
                                <DBTextArea
                                    rows={4}
                                    value={answers.exclusions}
                                    placeholder={t(
                                        "ai.itemtype.exclusions.placeholder"
                                    )}
                                    onChange={(event) =>
                                        updateAnswer(
                                            "exclusions",
                                            event.target.value
                                        )
                                    }
                                />
                            </DBFieldBlock>
                        </div>
                    </div>
                );
            case 4:
                return (
                    <div className="ai-step-grid">
                        <DBFieldBlock title={t("ai.itemtype.q5")} required>
                            <DBSelect
                                size="large"
                                value={answers.max_depth}
                                onChange={(value) =>
                                    updateAnswer("max_depth", value)
                                }
                                options={[
                                    {
                                        value: 2,
                                        label: t("ai.itemtype.depth.2"),
                                    },
                                    {
                                        value: 3,
                                        label: t("ai.itemtype.depth.3"),
                                    },
                                    {
                                        value: 4,
                                        label: t("ai.itemtype.depth.4"),
                                    },
                                ]}
                            />
                        </DBFieldBlock>
                    </div>
                );
            default:
                return (
                    <div className="ai-question-fields">
                        <DBFieldBlock
                            title={t("ai.experience.goal")}
                            required
                            error={error}
                        >
                            <DBTextArea
                                rows={3}
                                value={answers.feedback_goal}
                                placeholder={t("ai.experience.goal.placeholder")}
                                onChange={(event) =>
                                    updateAnswer(
                                        "feedback_goal",
                                        event.target.value
                                    )
                                }
                            />
                        </DBFieldBlock>
                        <div className="ai-step-grid">
                            <DBFieldBlock title={t("ai.form.response.style")}>
                                <DBSelect
                                    size="large"
                                    value={answers.response_style}
                                    onChange={(value) =>
                                        updateAnswer("response_style", value)
                                    }
                                    options={[
                                        {
                                            value: "mixed",
                                            label: t(
                                                "ai.form.response.style.mixed"
                                            ),
                                        },
                                        {
                                            value: "rating",
                                            label: t(
                                                "ai.form.response.style.rating"
                                            ),
                                        },
                                        {
                                            value: "text",
                                            label: t(
                                                "ai.form.response.style.text"
                                            ),
                                        },
                                    ]}
                                />
                            </DBFieldBlock>
                            <DBFieldBlock title={t("ai.form.q4")}>
                                <DBSelect
                                    size="large"
                                    value={answers.expertise_level}
                                    onChange={(value) =>
                                        updateAnswer("expertise_level", value)
                                    }
                                    options={[
                                        {
                                            value: "general",
                                            label: t(
                                                "ai.form.expertise.general"
                                            ),
                                        },
                                        {
                                            value: "semi_specialized",
                                            label: t(
                                                "ai.form.expertise.semi.specialized"
                                            ),
                                        },
                                        {
                                            value: "specialized",
                                            label: t(
                                                "ai.form.expertise.specialized"
                                            ),
                                        },
                                    ]}
                                />
                            </DBFieldBlock>
                        </div>
                        <DBFieldBlock title={t("ai.experience.mode")}>
                            <DBSelect
                                size="large"
                                value={mode}
                                onChange={(value) => setMode(value)}
                                options={[
                                    {
                                        value: "append",
                                        label: t("ai.experience.mode.append"),
                                    },
                                    {
                                        value: "replace",
                                        label: t("ai.experience.mode.replace"),
                                    },
                                ]}
                            />
                            {mode === "replace" && (
                                <p className="ai-mode-warning" role="alert">
                                    {t("ai.experience.mode.replace.warning")}
                                </p>
                            )}
                        </DBFieldBlock>
                    </div>
                );
        }
    };

    const renderGrouping = () => {
        if (genTreeLoader) {
            return (
                <div className="ai-phase-loading" aria-live="polite">
                    <DBSpinner />
                    <p>{t("ai.experience.generating.grouping")}</p>
                </div>
            );
        }
        if (!tree) {
            return (
                <div className="ai-phase-loading" aria-live="polite">
                    {error && <div className="ai-error">{error}</div>}
                    <p>{t("ai.experience.grouping.description")}</p>
                    <DBButton
                        caption={
                            <>
                                {t("ai.experience.start")}
                                <AiCoinHint operation="item_type_tree" />
                            </>
                        }
                        onClick={() => generateTree()}
                    />
                </div>
            );
        }
        return (
            <div className="ai-preview">
                <h3>{t("ai.experience.grouping.title")}</h3>
                <p>{t("ai.experience.grouping.description")}</p>
                {error && <div className="ai-error">{error}</div>}
                <div className="ai-toolbar">
                    <DBButton
                        caption={t("ai.experience.grouping.select.all")}
                        type="secondary"
                        onClick={() =>
                            setSelected(
                                new Set(flatGroups.map((group) => group.key))
                            )
                        }
                    />
                    <DBButton
                        caption={t("ai.experience.grouping.select.none")}
                        type="secondary"
                        onClick={() => setSelected(new Set())}
                    />
                    <DBButton
                        caption={t("ai.experience.grouping.add.root")}
                        type="secondary"
                        onClick={() =>
                            setTree([
                                ...tree,
                                {
                                    name: "",
                                    description: "",
                                    typ: 1,
                                    children: [],
                                },
                            ])
                        }
                    />
                </div>
                <ul className="ai-tree">
                    {tree.map((node, index) => (
                        <EditableTreeNode
                            key={index}
                            node={node}
                            nodeKey={`${index}`}
                            selected={selected}
                            onToggleSelect={toggleSelect}
                            onChange={(updatedNode) =>
                                setTree(
                                    tree.map((item, itemIndex) =>
                                        itemIndex === index
                                            ? updatedNode
                                            : item
                                    )
                                )
                            }
                            onDelete={() =>
                                setTree(
                                    tree.filter(
                                        (_, itemIndex) => itemIndex !== index
                                    )
                                )
                            }
                        />
                    ))}
                </ul>
                <div className="ai-feedback-row">
                    <DBInput
                        value={feedbackText}
                        placeholder={t("ai.experience.feedback.placeholder")}
                        onChange={(event) =>
                            setFeedbackText(event.target.value)
                        }
                    />
                    <DBButton
                        caption={
                            <>
                                {t("ai.experience.regenerate")}
                                <AiCoinHint operation="item_type_tree" />
                            </>
                        }
                        type="secondary"
                        loading={genTreeLoader}
                        onClick={() => generateTree(feedbackText.trim())}
                    />
                </div>
            </div>
        );
    };

    const renderFeatures = () => {
        if (featureProgress) {
            return (
                <div className="ai-phase-loading" aria-live="polite">
                    <DBSpinner />
                    <p>
                        {t("ai.experience.generating.features.progress", {
                            name: featureProgress.name,
                            current: featureProgress.current,
                            total: featureProgress.total,
                        })}
                    </p>
                </div>
            );
        }
        const missingFeatureKeys = selectedKeys.filter(
            (key) => !(features?.[key]?.length)
        );
        return (
            <div className="ai-preview">
                <h3>{t("ai.experience.features.title")}</h3>
                <p>{t("ai.experience.features.description")}</p>
                {error && <div className="ai-error">{error}</div>}
                {missingFeatureKeys.length > 0 && (
                    <div className="ai-phase-cta">
                        <DBButton
                            caption={
                                <>
                                    {t("ai.experience.generate.features")}
                                    <AiCoinHint
                                        operation="group_features"
                                        quantity={missingFeatureKeys.length}
                                    />
                                </>
                            }
                            loading={genFeaturesLoader}
                            onClick={() => generateFeatures(missingFeatureKeys)}
                        />
                    </div>
                )}
                {selectedKeys.map((key) => (
                        <GroupAccordion
                            key={key}
                            title={groupNames[key]}
                            defaultOpen={
                                !features?.[key]?.length ||
                                selectedKeys.length === 1
                            }
                            badge={t("ai.experience.confirm.features", {
                                count: features?.[key]?.length || 0,
                            })}
                        >
                        {(features?.[key] || []).length === 0 && (
                            <p className="preview-empty">
                                {t("ai.experience.features.empty")}
                            </p>
                        )}
                        {(features?.[key] || []).map((feature, index) => (
                            <FeatureRow
                                key={index}
                                feature={feature}
                                onChange={(updated) =>
                                    setFeatures({
                                        ...features,
                                        [key]: features[key].map(
                                            (item, itemIndex) =>
                                                itemIndex === index
                                                    ? updated
                                                    : item
                                        ),
                                    })
                                }
                                onDelete={() =>
                                    setFeatures({
                                        ...features,
                                        [key]: features[key].filter(
                                            (_, itemIndex) =>
                                                itemIndex !== index
                                        ),
                                    })
                                }
                            />
                        ))}
                        <DBButton
                            caption={t("ai.experience.features.add")}
                            type="secondary"
                            onClick={() =>
                                setFeatures({
                                    ...(features || {}),
                                    [key]: [
                                        ...(features?.[key] || []),
                                        {
                                            name: "",
                                            feature_type: 1,
                                            options: null,
                                        },
                                    ],
                                })
                            }
                        />
                    </GroupAccordion>
                ))}
                <div className="ai-feedback-row">
                    <DBInput
                        value={feedbackText}
                        placeholder={t("ai.experience.feedback.placeholder")}
                        onChange={(event) =>
                            setFeedbackText(event.target.value)
                        }
                    />
                    <DBButton
                        caption={
                            <>
                                {t("ai.experience.regenerate")}
                                <AiCoinHint
                                    operation="group_features"
                                    quantity={Math.max(1, selectedKeys.length)}
                                />
                            </>
                        }
                        type="secondary"
                        loading={genFeaturesLoader}
                        onClick={() =>
                            generateFeatures(
                                selectedKeys,
                                feedbackText.trim() || undefined
                            )
                        }
                    />
                </div>
            </div>
        );
    };

    const renderForms = () => {
        if (formProgress) {
            return (
                <div className="ai-phase-loading" aria-live="polite">
                    <DBSpinner />
                    <p>
                        {t("ai.experience.generating.forms", {
                            name: formProgress.name,
                            current: formProgress.current,
                            total: formProgress.total,
                        })}
                    </p>
                </div>
            );
        }
        return (
            <div className="ai-preview">
                <h3>{t("ai.experience.forms.title")}</h3>
                <p>{t("ai.experience.forms.description")}</p>
                {error && <div className="ai-error">{error}</div>}
                {selectedKeys.some((key) => !forms?.[key]) && (
                    <div className="ai-phase-cta">
                        <DBButton
                            caption={
                                <>
                                    {t("ai.experience.generate.forms")}
                                    <AiCoinHint
                                        operation="form_structure"
                                        quantity={Math.max(
                                            1,
                                            selectedKeys.filter(
                                                (key) => !forms?.[key]
                                            ).length
                                        )}
                                    />
                                </>
                            }
                            loading={genFormsLoader}
                            onClick={() =>
                                generateForms(
                                    selectedKeys.filter((key) => !forms?.[key])
                                )
                            }
                        />
                    </div>
                )}
                {selectedKeys.map((key) => {
                    const form = forms?.[key];
                    return (
                        <GroupAccordion
                            key={key}
                            title={groupNames[key]}
                            defaultOpen={!form || selectedKeys.length === 1}
                            badge={
                                form
                                    ? t("ai.experience.criteria.count", {
                                          count: form.criteria.length,
                                      })
                                    : undefined
                            }
                        >
                            {!form ? (
                                <p className="preview-empty">
                                    {t("ai.experience.features.empty")}
                                </p>
                            ) : (
                                <div className="ai-form-card">
                                    <div className="ai-form-header">
                                        <DocumentText
                                            size={18}
                                            aria-hidden="true"
                                        />
                                        <DBInput
                                            value={form.name}
                                            onChange={(event) =>
                                                setForms({
                                                    ...forms,
                                                    [key]: {
                                                        ...form,
                                                        name: event.target
                                                            .value,
                                                    },
                                                })
                                            }
                                        />
                                    </div>
                                    <ul className="preview-criteria">
                                        {form.criteria.map(
                                            (criterion, criterionIndex) => (
                                                <li key={criterionIndex}>
                                                    <DBInput
                                                        value={criterion.name}
                                                        onChange={(event) =>
                                                            setForms({
                                                                ...forms,
                                                                [key]: {
                                                                    ...form,
                                                                    criteria:
                                                                        form.criteria.map(
                                                                            (
                                                                                item,
                                                                                itemIndex
                                                                            ) =>
                                                                                itemIndex ===
                                                                                criterionIndex
                                                                                    ? {
                                                                                          ...item,
                                                                                          name: event
                                                                                              .target
                                                                                              .value,
                                                                                      }
                                                                                    : item
                                                                        ),
                                                                },
                                                            })
                                                        }
                                                    />
                                                    <span className="preview-chip muted">
                                                        {t(
                                                            CRITERIA_TYPE_LABELS[
                                                                criterion
                                                                    .criteria_state_type
                                                            ] ||
                                                                "ai.experience.criteria.type.2"
                                                        )}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        className="ai-tree-delete"
                                                        aria-label={t(
                                                            "ai.form.remove.criterion"
                                                        )}
                                                        title={t(
                                                            "ai.form.remove.criterion"
                                                        )}
                                                        onClick={() =>
                                                            setForms({
                                                                ...forms,
                                                                [key]: {
                                                                    ...form,
                                                                    criteria:
                                                                        form.criteria.filter(
                                                                            (
                                                                                _,
                                                                                itemIndex
                                                                            ) =>
                                                                                itemIndex !==
                                                                                criterionIndex
                                                                        ),
                                                                },
                                                            })
                                                        }
                                                    >
                                                        <Trash
                                                            size={16}
                                                            aria-hidden="true"
                                                        />
                                                    </button>
                                                </li>
                                            )
                                        )}
                                    </ul>
                                    <div className="ai-form-actions">
                                        <DBButton
                                            caption={
                                                <>
                                                    {t(
                                                        "ai.experience.forms.regenerate.group"
                                                    )}
                                                    <AiCoinHint operation="form_structure" />
                                                </>
                                            }
                                            type="secondary"
                                            loading={genFormsLoader}
                                            onClick={() =>
                                                generateForms(
                                                    [key],
                                                    feedbackText.trim() ||
                                                        " "
                                                )
                                            }
                                        />
                                        <DBButton
                                            caption={t(
                                                "ai.experience.forms.remove.group"
                                            )}
                                            type="secondary"
                                            onClick={() => {
                                                const next = {
                                                    ...(forms || {}),
                                                };
                                                delete next[key];
                                                setForms(next);
                                            }}
                                        />
                                    </div>
                                </div>
                            )}
                        </GroupAccordion>
                    );
                })}
                <div className="ai-feedback-row">
                    <DBInput
                        value={feedbackText}
                        placeholder={t("ai.experience.feedback.placeholder")}
                        onChange={(event) =>
                            setFeedbackText(event.target.value)
                        }
                    />
                    <DBButton
                        caption={
                            <>
                                {t("ai.experience.regenerate")}
                                <AiCoinHint
                                    operation="form_structure"
                                    quantity={Math.max(1, selectedKeys.length)}
                                />
                            </>
                        }
                        type="secondary"
                        loading={genFormsLoader}
                        onClick={() =>
                            generateForms(
                                selectedKeys,
                                feedbackText.trim() || undefined
                            )
                        }
                    />
                </div>
            </div>
        );
    };

    const renderConfirm = () => {
        const featureCount = selectedKeys.reduce(
            (total, key) => total + (features?.[key]?.length || 0),
            0
        );
        const formList = selectedKeys
            .map((key) => forms?.[key])
            .filter(Boolean);
        const criteriaCount = formList.reduce(
            (total, form) => total + form.criteria.length,
            0
        );
        return (
            <div className="ai-preview ai-confirm">
                <h3>{t("ai.experience.confirm.title")}</h3>
                <p>{t("ai.experience.confirm.description")}</p>
                <ul className="ai-summary">
                    <li>
                        {t("ai.experience.confirm.groups", {
                            count: selectedKeys.length,
                        })}
                    </li>
                    <li>
                        {t("ai.experience.confirm.features", {
                            count: featureCount,
                        })}
                    </li>
                    <li>
                        {t("ai.experience.confirm.forms", {
                            count: formList.length,
                        })}
                    </li>
                    <li>
                        {t("ai.experience.confirm.criteria", {
                            count: criteriaCount,
                        })}
                    </li>
                </ul>
                <p className="ai-mode-note">
                    {t(
                        mode === "replace"
                            ? "ai.experience.confirm.mode.replace"
                            : "ai.experience.confirm.mode.append"
                    )}
                </p>
            </div>
        );
    };

    const renderPhase = () => {
        if (resumePrompt) {
            return (
                <div className="ai-resume">
                    <h3>{t("ai.experience.resume.title")}</h3>
                    <p>{t("ai.experience.resume.description")}</p>
                    <div className="ai-resume-actions">
                        <DBButton
                            caption={t("ai.experience.resume.continue")}
                            onClick={() => {
                                setResumePrompt(false);
                                loadFromSession(pendingSession);
                            }}
                        />
                        <DBButton
                            caption={t("ai.experience.resume.restart")}
                            type="secondary"
                            onClick={() => {
                                setResumePrompt(false);
                                if (pendingSession) {
                                    deleteSession(
                                        null,
                                        null,
                                        `${pendingSession.id}`
                                    ).catch(() => {});
                                    setPendingSession(null);
                                }
                            }}
                        />
                    </div>
                </div>
            );
        }
        switch (phase) {
            case PHASE_QUESTIONS:
                return (
                    <>
                        <div className="ai-progress">
                            <span>
                                {t("ai.experience.step", {
                                    step: questionStep + 1,
                                })}
                            </span>
                            <div
                                role="progressbar"
                                aria-valuenow={questionStep + 1}
                                aria-valuemin={1}
                                aria-valuemax={6}
                            >
                                {[0, 1, 2, 3, 4, 5].map((item) => (
                                    <i
                                        key={item}
                                        className={
                                            item <= questionStep
                                                ? "active"
                                                : ""
                                        }
                                    />
                                ))}
                            </div>
                        </div>
                        <div className="ai-question">{renderQuestion()}</div>
                    </>
                );
            case PHASE_GROUPING:
                return renderGrouping();
            case PHASE_FEATURES:
                return renderFeatures();
            case PHASE_FORMS:
                return renderForms();
            default:
                return renderConfirm();
        }
    };

    const renderFooter = () => {
        if (resumePrompt) return null;
        if (phase === PHASE_QUESTIONS) {
            return (
                <>
                    {questionStep === 5 ? (
                        <DBButton
                            caption={
                                <>
                                    {t("ai.experience.start")}
                                    <AiCoinHint operation="item_type_tree" />
                                </>
                            }
                            onClick={startGeneration}
                            loading={createLoader || patchLoader}
                        />
                    ) : (
                        <DBButton
                            caption={t("ai.itemtype.next")}
                            onClick={() => {
                                if (validateQuestionStep())
                                    setQuestionStep(questionStep + 1);
                            }}
                        />
                    )}
                    {questionStep > 0 && (
                        <DBButton
                            caption={t("ai.itemtype.previous")}
                            type="secondary"
                            onClick={goPrevious}
                        />
                    )}
                </>
            );
        }
        if (phase === PHASE_CONFIRM) {
            return (
                <>
                    <DBButton
                        caption={t("ai.experience.save")}
                        onClick={finalApply}
                        loading={applyLoader || patchLoader}
                    />
                    <DBButton
                        caption={t("ai.experience.previous")}
                        type="secondary"
                        onClick={goPrevious}
                    />
                    {session && (
                        <DBButton
                            caption={t("ai.experience.abandon")}
                            type="secondary"
                            onClick={() => setAbandonConfirm(true)}
                        />
                    )}
                </>
            );
        }
        return (
            <>
                <DBButton
                    caption={t("ai.experience.next")}
                    onClick={goNext}
                    loading={patchLoader}
                    disabled={
                        generating ||
                        Boolean(formProgress) ||
                        Boolean(featureProgress) ||
                        coinGateOpen
                    }
                />
                <DBButton
                    caption={t("ai.experience.save.later")}
                    type="secondary"
                    onClick={saveAndContinueLater}
                    loading={applyLoader || patchLoader}
                    disabled={
                        !tree?.length ||
                        generating ||
                        Boolean(formProgress) ||
                        Boolean(featureProgress) ||
                        coinGateOpen
                    }
                />
                <DBButton
                    caption={t("ai.experience.previous")}
                    type="secondary"
                    onClick={goPrevious}
                />
                {session && (
                    <DBButton
                        caption={t("ai.experience.abandon")}
                        type="secondary"
                        onClick={() => setAbandonConfirm(true)}
                    />
                )}
            </>
        );
    };

    return (
        <>
        <DBModal
            permanent
            show={showModal}
            onHide={onHide}
            className="ai-experience-modal"
        >
            <DBModal.Header
                closeBtn={onHide}
                title={t("ai.experience.title")}
            >
                <MagicStar size={24} aria-hidden="true" />
            </DBModal.Header>
            <DBModal.Content>
                {!resumePrompt && (
                    <nav
                        className="ai-stepper"
                        aria-label={t("ai.experience.title")}
                    >
                        {PHASES.map((item, index) => (
                            <span
                                key={item}
                                className={[
                                    "ai-stepper-step",
                                    index === phase ? "active" : "",
                                    index < phase ? "done" : "",
                                ]
                                    .filter(Boolean)
                                    .join(" ")}
                                aria-current={index === phase ? "step" : undefined}
                            >
                                {t(`ai.experience.stepper.${item}`)}
                            </span>
                        ))}
                    </nav>
                )}
                {renderPhase()}
            </DBModal.Content>
            <DBModal.Footer>
                {renderFooter()}
                <DBButton
                    caption={t("cancel")}
                    type="secondary"
                    onClick={onHide}
                />
            </DBModal.Footer>
            <DBConfirmModal
                title={t("ai.experience.abandon")}
                description={t("ai.experience.abandon.confirm")}
                btnTitle={t("ai.experience.abandon")}
                onHide={() => setAbandonConfirm(false)}
                show={abandonConfirm}
                minWidth={400}
                onConfirm={abandonDraft}
            />
        </DBModal>
        {gate}
        </>
    );
}
